import { useEffect, useRef, useCallback } from 'react';
import { useLobsterStore } from '../store/useLobsterStore';
import type { Device } from './useDeviceDiscovery';

const POLL_INTERVAL = 2000;
const RECONNECT_THRESHOLD = 3; // After N consecutive failures, trigger device refresh

function buildUrl(device: Device, path: string): string {
  const base = device.tunnel_url.replace(/\/$/, '');
  const sep = path.includes('?') ? '&' : '?';
  return `${base}${path}${sep}access_token=${encodeURIComponent(device.access_token)}`;
}

export function useRemoteAgent(
  device: Device | null,
  onDeviceStale?: () => void,
) {
  const { setAgents, setConnection, activeAgentId, addMessages } = useLobsterStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTimestampRef = useRef<Record<string, number>>({});
  const failCountRef = useRef(0);

  const fetchAgents = useCallback(async () => {
    if (!device) { setConnection('offline'); return; }

    try {
      const res = await fetch(buildUrl(device, '/api/agents'));
      if (res.ok) {
        const data = await res.json();
        const agents = (data.agents || []).map((a: Record<string, unknown>) => ({
          id: a.id as string,
          accountId: (a.id || a.sessionId) as string,
          name: (a.name || 'Agent') as string,
          status: a.status as string,
          currentTask: a.currentTask as string,
          progress: (a.progress as number) || 0,
          description: (a.cwd || '') as string,
          lastActivity: new Date(a.lastActivity as number),
          source: a.source as string,
          tool: a.tool as string,
          icon: a.icon as string,
        }));
        setAgents(agents);
        setConnection('online');
        failCountRef.current = 0;
      } else {
        setConnection('offline');
        failCountRef.current++;
      }
    } catch {
      setConnection('offline');
      failCountRef.current++;
    }

    // After consecutive failures, ask parent to refresh device info from cloud
    if (failCountRef.current >= RECONNECT_THRESHOLD && onDeviceStale) {
      console.log('[RemoteAgent] Tunnel unreachable, requesting device refresh');
      failCountRef.current = 0; // Reset to avoid spamming
      onDeviceStale();
    }
  }, [device, setAgents, setConnection, onDeviceStale]);

  const fetchMessages = useCallback(async () => {
    if (!device || !activeAgentId) return;

    try {
      const since = lastTimestampRef.current[activeAgentId] || 0;
      const res = await fetch(buildUrl(device, `/api/agent/messages?accountId=${encodeURIComponent(activeAgentId)}&since=${since}`));
      if (!res.ok) return;

      const data = await res.json();
      if (data.messages?.length > 0) {
        addMessages(activeAgentId, data.messages);
        const maxTs = Math.max(...data.messages.map((m: { timestamp: number }) => m.timestamp));
        lastTimestampRef.current[activeAgentId] = maxTs;
      }
    } catch { /* silent */ }
  }, [device, activeAgentId, addMessages]);

  useEffect(() => {
    if (!device) return;

    failCountRef.current = 0;
    fetchAgents();
    fetchMessages();

    intervalRef.current = setInterval(() => {
      fetchAgents();
      fetchMessages();
    }, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [device, fetchAgents, fetchMessages]);

  useEffect(() => {
    fetchMessages();
  }, [activeAgentId, fetchMessages]);

  // Send message through tunnel
  const sendMessage = useCallback(async (text: string, accountId: string): Promise<boolean> => {
    if (!device) return false;
    try {
      const res = await fetch(buildUrl(device, '/api/user/message'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, accountId }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }, [device]);

  // Respond to approval through tunnel
  const respondApproval = useCallback(async (requestId: string, decision: 'approved' | 'rejected'): Promise<boolean> => {
    if (!device) return false;
    try {
      const res = await fetch(buildUrl(device, '/api/user/approval-response'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, decision }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }, [device]);

  return { sendMessage, respondApproval };
}
